import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConsorciosManagerService } from '@pages/consorcios/consorcios-manager.service';
import { CocheraNivel, Consorcio, withCocheraLabel } from '@pages/consorcios/consorcios.config';
import { CocheraNombrePipe } from '@shared/pipes/cochera-nombre.pipe';
import { ConsorciosService } from '@pages/consorcios/consorcios.service';
import {  ConsorcistaPending, ConsorcistaRole, ConsorcistasService, OccupancyRole, PendingInteractionType } from '@pages/consorcios/consorcistas.service';
import { SignupConsorcistaToken } from '@pages/consorcios/consorcistaTokens.config';
import { consorcistasTokenService } from '@pages/consorcios/consorcistaTokens.services';
import { ConsorcistaTransfer, newTransfer } from '@pages/consorcios/transfers.config';
import { CocherasViewComponent } from '@shared/components/cocheras-view/cocheras-view.component';
import { ConsorcioSchemeComponent } from '@shared/components/consorcio-scheme/consorcio-scheme.component';
import { UserService } from '@shared/modules/auth/services/user.service';
import { SharedPrimeNgModule } from '@shared/modules/shared-prime-ng.module';
import { LoaderStoreService } from '@shared/services/loader-store.service';
import { NotificationService } from '@shared/services/messages/notification.service';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { firstValueFrom, Observable } from 'rxjs';
import { Countries } from 'src/app/models/countries';

interface ErrorMessage {
    severity: 'success' | 'error';
    detail: string;
    action?: () => void;
}
type stepName = 'welcomeStep' | 'nameStep' | 'typeStep' | 'stepRealEstate' | 'stepContrato' | 'unitStep' | 'emailStep' | 'cocheraStep' | 'ifCocheraStep' | 'last' | 'phoneStep' | 'movingStep'| 'replaceStep' | 'listConsorcistaStep' | 'addConsorcistaStep' | 'dataListStep' | 'sameOwnersStep' | 'queryStep'  ;


@Component({
    selector: 'app-form-consorcista',
    standalone: true,
    imports: [
        PasswordModule,
        FormsModule,
        CheckboxModule,
        ButtonModule,
        RouterLink,
        ReactiveFormsModule,
        SharedPrimeNgModule,
        CommonModule,
        ConsorcioSchemeComponent,
        CocherasViewComponent,
        CocheraNombrePipe,
        DialogModule
    ],
    templateUrl: './form-consorcista.component.html',
    styleUrl: './form-consorcista.component.scss'
})
export class FormConsorcistaComponent {
    form!: FormGroup;
    paises=Countries;
    fb = inject(FormBuilder);
    errorMessage?: ErrorMessage;
    showFinishDialog: boolean = false;
    router = inject(Router);
    loaderStore = inject(LoaderStoreService);
    route = inject(ActivatedRoute);
    consorcistasService = inject(ConsorcistasService);
    userService = inject(UserService);
    consorcistaToken?: SignupConsorcistaToken;
    signupTokenService = inject(consorcistasTokenService);
    consorcioService = inject(ConsorciosService);
    actualStep: stepName = 'welcomeStep';
    stepsTaken: number = 1;
    signToken$!: Observable<SignupConsorcistaToken>;
    code!: any;
    consorcio?: Consorcio;
    consorcio$: Observable<any>;
    stepsTotalBase: number = 7;
    stepsRemaining: number = this.stepsTotalBase;
    stepsStack: stepName[] = [];
    consorcioManager = inject(ConsorciosManagerService);
    consorcistasToTransfer: ConsorcistaTransfer[] = [];
    consorcistaTypeOptions = [
        {value: 'owner',label: 'Propietario'},
        {value: 'tenant',label: 'Inquilino'},
        {value: 'realEstate',label: 'Inmobiliaria'}
    ];
    rolesCocheraOptions = [
        {value: 'owner',label: 'Soy dueño'},
        {value: 'tenant',label: 'Alquilo'}
    ];
    consorcistaPendiente: Partial<ConsorcistaPending> = {};
    confirmationService = inject(ConfirmationService);
    notificationService = inject(NotificationService);
    //pasos extra
    extraStepType:boolean = false;
    extraStepCochera:boolean = false;
    extraStepIfCochera:boolean = false;
    replaceOptions = {
        'owner':[
            {label: 'Sí, hubo un cambio de propietarios. Soy el primero en completar este formulario.', value: 'moving'},
            {label: 'Sí, hubo un cambio de propietarios. Otro propietario ya completó este formulario.', value: 'adding_other_first_owner'},
            {label: 'No. Otro propietario ya completó este formulario.', value: 'adding_other_owner'},
        ],
        'realEstate':[
            {label: 'Sí, reemplazo a la inmobiliaria anterior', value: 'moving'},
            {label: 'No, me sumo a la inmobiliaria anterior', value: 'adding'}
        ],
        'tenant':[
            {label: 'Sí, reemplazo a los ocupantes anteriores', value: 'moving'},
            {label: 'No, vivo con otro ocupante que ya completó este formulario', value: 'adding_other_tenant'},
            {label: 'No, no hubo ninguna mudanza', value: 'adding_tenant'}
        ],
    };
    translate = inject(TranslateService);
     _t = (key: string) => this.translate.instant(key);

    viewNivel:string ='';
    nivelCocheraToShow: (CocheraNivel & { units: number });
    consorcistaTransferTemp: ConsorcistaTransfer ;
    showAddCocheraDialog:boolean = false;
    // es para indicar que ya se agregó una vez a la coleccion de consorcistas de la mudanza, es para cuando volvés a atrás y no se agregue de nuevo
    consorcistaAdded:boolean = false;
    existingOccupants: number = 0;

    async ngOnInit() {
        this.form = this.fb.group({
            nameStep: this.fb.group({
                firstName: ['', [Validators.required]],
                lastName: ['', Validators.required],
            }),
            typeStep: this.fb.group({
                consorcistaType: ['', Validators.required],
            }),
            stepRealEstate: this.fb.group({
                realEstateName: ['', Validators.required],
            }),
            stepContrato: this.fb.group({
                contrato: ['', Validators.required],
            }),
            unitStep: this.fb.group({
                tower: [''],
                unit: ['', Validators.required],
                floor: ['', Validators.required],
                onlyGarage: [false, Validators.required]
            }),
            phoneStep: this.fb.group({
                countryCode: [''],
                phoneArea: ['', Validators.required],
                phoneNumber: ['', Validators.required],
                country: ['', Validators.required]
            }),
            stepUnitInfo: this.fb.group({
                apartmentFloor: ['', Validators.required],
                apartmentUnit: ['', Validators.required],
            }),
            stepRealEstateOtherUnits: this.fb.group({
                gestionOtherUnits: [false, Validators.required],

            }),
            cocheraStep: this.fb.group({
                cocheraLevel: ['', Validators.required],
                cocheraNumber: [0, Validators.required],
                cocheraRole: ['', Validators.required],
                hasGarage: [false, Validators.required],
            }),
            emailStep: this.fb.group({
                email: ['', [Validators.required, Validators.email]],

            }),
            stepTypeOfRent: this.fb.group({
                typeOfRent: ['', [Validators.required]]
            }),
            stepReplace:this.fb.group({ 
                isReplacing: ['', [Validators.required]],
            }), 
            stepListaConsorcista:this.fb.group({
                    firstName: this.fb.control('', Validators.required),
                    lastName: this.fb.control('', Validators.required),
                    countryCode: this.fb.control('', Validators.required),
                    phoneArea: this.fb.control('', Validators.required),
                    phoneNumber: this.fb.control('', Validators.required),
            }),
            sameOwnersStep:this.fb.group({ 
                sameOwners: [false, Validators.required]
            }),
            queryStep: this.fb.group({
                interactionType: [''],
                interactionText: [''],
            }),
            addConsorcistaStep:this.fb.group({
                newConsorcistaFirstName: this.fb.control('', Validators.required),
                newConsorcistaLastName: this.fb.control('', Validators.required),   
                newConsorcistaCountryCode: this.fb.control(''),
                newConsorcistaPhoneArea: this.fb.control('', Validators.required),
                newConsorcistaPhoneNumber: this.fb.control('', Validators.required),
                newConsorcistahasGarage: this.fb.control(false, Validators.required),
                newConsorcistaCochera:this.fb.group({
                    nivel: this.fb.control(''),
                    numero: this.fb.control(''),
                    occupancyRole: this.fb.control(''),
                }),

            })



        })
        this.code = this.route.snapshot.queryParams['cod']

        if(!this.code){
            this.errorMessage = {
                severity: 'error',
                detail: 'Falta el código de registro. Por favor, verifique el enlace que recibió o comuníquese con la administración del consorcio para obtener asistencia.',
                action: () => {
                    // this.router.navigate(['/']);
                }
            };
            return;
        }

        this.signToken$ = this.signupTokenService.getTokenByCod(this.code);
        this.consorcistaToken = await firstValueFrom(this.signToken$);

        //revisar antigüedad del token, si es mayor a 30minutos, mostrar error y no permitir continuar
        const createdAt = this.consorcistaToken?.createdAt instanceof Date ? this.consorcistaToken.createdAt : this.consorcistaToken?.createdAt.toDate();
        if(this.consorcistaToken && (new Date().getTime() - createdAt.getTime()) > 30 * 60 * 1000){
            this.errorMessage = {
                severity: 'error',
                detail: 'El código de registro ha expirado. Por favor, solicite un nuevo enlace a la administración del consorcio.',
                action: () => {
                    // this.router.navigate(['/']);
                }
            };
            return;
        }

        if (this.consorcistaToken) {
            await this.getConsorcio(this.consorcistaToken.consorcioId, this.consorcistaToken.administrationId);
            if(this.consorcistaToken.firstName){
                this.form.get('nameStep.firstName').setValue(this.consorcistaToken.firstName);
            }
            if(this.consorcistaToken.lastName){
                this.form.get('nameStep.lastName').setValue(this.consorcistaToken.lastName);
            }

            
        }

        if (!this.consorcistaToken || !this.consorcio) {
            this.errorMessage = {
                severity: 'error',
                detail: 'Código de registro inválido. Por favor, verifique el enlace que recibió o comuníquese con la administración del consorcio para obtener asistencia.',
                action: () => {
                    // this.router.navigate(['/']);
                }
            };
        }
    }

    async getConsorcio(uid: string, id: string) {
        try {
            this.loaderStore.set('loading', true);
            this.consorcio$ = this.consorcioService.getOneByAdmin(uid, id)
            this.consorcio = await firstValueFrom(this.consorcio$)

        } catch (error) {
        } finally {
            this.loaderStore.set('loading', false);
        }
    }
    nextStep(nextStep: stepName) {

        this.stepsTaken++;
        this.stepsStack.push(this.actualStep);
        this.actualStep = nextStep;

    }
    goStepBack() {
        this.actualStep = this.stepsStack[this.stepsStack.length - 1];
        this.stepsStack.pop()
        this.stepsTaken--;

    }
    async fillUnitStepForm(event: any) {
        // llenar formulario
        if(!this.form.get('unitStep.onlyGarage').value){
            if (this.consorcio.hasTowers) {
                this.form.get('unitStep.tower').setValue(event.tower);
            }
            this.form.get('unitStep.unit').setValue(event.apartment);
            this.form.get('unitStep.floor').setValue(event.floor);
        }else{
            if (this.consorcio.hasTowers) {
                this.form.get('unitStep.tower').setValue('');
            }
            this.form.get('unitStep.unit').setValue('');
            this.form.get('unitStep.floor').setValue('');
        }
        // revisar si hay ocupantes
        this.existingOccupants = await this.consorcistasService.countByUnitAndRole(
            this.consorcio.uid,
            this.form.get('unitStep.floor').value,
            this.form.get('unitStep.unit').value,
            this.form.get('typeStep.consorcistaType').value,
            this.consorcio.hasTowers ? this.form.get('unitStep.tower').value : undefined
        );
    }

    fillCocheraStepForm(event:{nivel:number, numero:number}) {
        this.form.get('cocheraStep.cocheraLevel').setValue(event.nivel);
        this.form.get('cocheraStep.cocheraNumber').setValue(event.numero);
    }

    onSubmit() {
        switch (this.actualStep) {
            case ('welcomeStep'):
                this.nextStep('nameStep')
                break
            case 'nameStep':
                if (!this.form.get('nameStep')?.valid) {
                    this.form.get('nameStep')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    this.consorcistaPendiente.firstName = this.form.get('nameStep.firstName').value;
                    this.consorcistaPendiente.lastName = this.form.get('nameStep.lastName').value;

                    this.nextStep('typeStep')
                }
                break
            case 'typeStep':
                
                if (!this.form.get('typeStep')?.valid) {
                    this.form.get('typeStep')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    this.extraStepType = false;
                    this.consorcistaPendiente.occupancyRole = this.form.get('typeStep.consorcistaType').value;

                    switch (this.form.get('typeStep.consorcistaType').value) {
                        case 'realEstate':
                            this.extraStepType = true;
                            this.nextStep('stepRealEstate')
                            break;
                        case 'owner':
                            this.extraStepType = false;
                            this.nextStep('unitStep')
                            break;
                        case 'tenant':
                            this.extraStepType = true;
                            this.nextStep('stepContrato')
                            break;
                    }
                }
                break
            case 'stepRealEstate':
                if (!this.form.get('stepRealEstate')?.valid) {
                    this.form.get('stepRealEstate')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    this.consorcistaPendiente.realEstate = {
                        companyName: this.form.get('stepRealEstate.realEstateName').value,
                        phoneArea: '',
                        phoneNumber: '',
                    }
                    this.nextStep('unitStep')
                }
                break
            case 'stepContrato':
                if (!this.form.get('stepContrato')?.valid) {
                    this.form.get('stepContrato')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    this.consorcistaPendiente.contractType = this.form.get('stepContrato.contrato').value;
                    this.nextStep('unitStep')
                }
                break
            case 'unitStep':
                if (!this.form.get('unitStep')?.valid && !this.form.get('unitStep.onlyGarage')?.value) {
                    this.form.get('unitStep')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                }else if(this.form.get('unitStep.onlyGarage')?.value && !this.consorcio.cocheras){
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'No hay cocheras disponibles en este consorcio.',
                    };
                } else {

                    if(this.form.get('unitStep.onlyGarage')?.value) {
                        this.consorcistaPendiente.onlyGarage = true;
                    }else{
                        this.consorcistaPendiente.apartment = this.form.get('unitStep.unit').value;
                        this.consorcistaPendiente.floor = this.form.get('unitStep.floor').value;
                        if(this.form.get('unitStep.tower')?.value){
                            this.consorcistaPendiente.tower = this.form.get('unitStep.tower').value;
                        }
                    }

                    if (this.consorcio.cocheras) {
                        if(this.form.get('unitStep.onlyGarage')?.value){
                            this.nextStep('cocheraStep');
                        }else{
                            //si es de solo cochera no es necesario preguntar
                            this.extraStepIfCochera = true;
                            this.nextStep('ifCocheraStep');
                        }
                    } else {
                         this.extraStepIfCochera = false;
                        this.nextStep('emailStep');
                    }

                }
            break;
            case 'ifCocheraStep':
                if (this.form.get('cocheraStep.hasGarage').value) {
                    this.nextStep('cocheraStep');
                    this.extraStepCochera = true;
                } else {
                    this.nextStep('emailStep');
                    this.extraStepCochera = false;
                }
                break;
            case 'cocheraStep':
                if (!this.form.get('cocheraStep')?.valid) {
                    this.form.get('cocheraStep')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    //se guarda tambien la etiqueta de la cochera (si tiene)
                    this.consorcistaPendiente.cocheras = [withCocheraLabel({
                        nivel: this.form.get('cocheraStep.cocheraLevel').value,
                        numero: this.form.get('cocheraStep.cocheraNumber').value,
                        occupancyRole: this.form.get('cocheraStep.cocheraRole').value,
                        createdAt: new Date(),
                    }, this.consorcio?.cocheras)]
                    this.nextStep('emailStep')
                }
                break;
            case 'emailStep':
                if (!this.form.get('emailStep')?.valid) {
                    this.form.get('emailStep')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    this.consorcistaPendiente.email = this.form.get('emailStep.email').value;
                    this.nextStep('phoneStep')
                }
                break;
            case 'phoneStep':
                if (!this.form.get('phoneStep')?.valid) {
                    this.form.get('phoneStep')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {
                    this.consorcistaPendiente.phoneArea = this.form.get('phoneStep.phoneArea').value;
                    this.consorcistaPendiente.phoneNumber = this.form.get('phoneStep.phoneNumber').value;
                    this.consorcistaPendiente.countryCode = Number(this.form.value.phoneStep.country.phoneCode).toString();
                    this.nextStep('dataListStep')
                }
                break;
            case 'dataListStep':
                if(this.form.get('unitStep.onlyGarage')?.value
                || this.form.get('typeStep.consorcistaType').value == 'realEstate'
                || this.existingOccupants == 0
                ){
                    this.nextStep('queryStep')
                }
                else{
                    this.nextStep('replaceStep')
                }
                break;
            case 'replaceStep':
                if (!this.form.get('stepReplace')?.valid) {
                    this.form.get('stepReplace')?.markAllAsTouched();
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                } else {

                    if(this.form.get('stepReplace.isReplacing').value == 'moving') {
                        if(!this.consorcistaAdded){
                            this.consorcistaAdded = true;
                            let newconsorcista = {
                                firstName: this.consorcistaPendiente.firstName,
                                lastName: this.consorcistaPendiente.lastName,
                                email: this.consorcistaPendiente.email,
                                phoneArea: this.consorcistaPendiente.phoneArea,
                                phoneNumber: this.consorcistaPendiente.phoneNumber, 
                                countryCode: this.consorcistaPendiente.countryCode,
                                floor: this.consorcistaPendiente.floor,
                                apartment: this.consorcistaPendiente.apartment,
                                occupancyRole: this.consorcistaPendiente.occupancyRole,
                            };

                            if(this.consorcistaPendiente.cocheras) {
                                newconsorcista['cocheras'] = this.consorcistaPendiente.cocheras;
                            }

                            if(this.consorcio.hasTowers){
                                newconsorcista['tower'] = this.consorcistaPendiente.tower;
                            }

                            this.consorcistasToTransfer.push(newconsorcista);
                        }
                        this.nextStep('listConsorcistaStep');
                    }
                    else{
                        this.nextStep('queryStep')
                    }
                }
                break;
            //paso compartido: se llega acá tanto desde el alta directa (dataListStep/replaceStep) como desde el
            //flujo de mudanza (listConsorcistaStep), por eso al confirmar hay que distinguir con this.consorcistaAdded
            //a cuál de los dos flujos pertenece la respuesta.
            case 'queryStep': {
                const interactionText = this.form.get('queryStep.interactionText').value?.trim();
                const interactionType = this.form.get('queryStep.interactionType').value;
                if (interactionText && !interactionType) {
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, elegí una opción.',
                    };
                    break;
                }
                if (interactionText) {
                    //'repair' y 'complain' generan igualmente un reclamo (Complain), solo cambia la opción que ve el consorcista
                    const type: PendingInteractionType = interactionType === 'question' ? 'question' : 'complain';
                    this.consorcistaPendiente.pendingInteraction = { type, text: interactionText };
                }
                this.confirmationService.confirm({
                        message: '¿Está seguro de que desea enviar el formulario?',
                        accept: () => {
                        //si viene del flujo de mudanza, se envía como transferencia en vez de alta directa
                        if(this.consorcistaAdded){
                            this.submitTransfer();
                        }else{
                            this.submitForm();
                        }
                    }
                });
                break;
            }
            case 'addConsorcistaStep':
                //verificaciones: que esten todos los requeridos y que si tiene una cochera esten sus campos
                if(!this.form.get('addConsorcistaStep')?.valid){
                    this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente.',
                    };
                }else if(
                    this.form.get('addConsorcistaStep.newConsorcistahasGarage')?.value &&
                    (
                        !this.form.get('addConsorcistaStep.newConsorcistaCochera.nivel')?.value ||
                        !this.form.get('addConsorcistaStep.newConsorcistaCochera.numero')?.value ||
                        !this.form.get('addConsorcistaStep.newConsorcistaCochera.occupancyRole')?.value
                    )
                ){
                     this.errorMessage = {
                        severity: 'error',
                        detail: 'Por favor, complete todos los campos correctamente. Faltan campos de la cochera.',
                    };
                }else{
                    //agregar consorcista a la lista de consorcistas a transferir
                    let newconsorcista = {
                        firstName: this.form.get('addConsorcistaStep.newConsorcistaFirstName').value,
                        lastName: this.form.get('addConsorcistaStep.newConsorcistaLastName').value,
                        phoneArea: this.form.get('addConsorcistaStep.newConsorcistaPhoneArea').value,
                        phoneNumber: this.form.get('addConsorcistaStep.newConsorcistaPhoneNumber').value, 
                        countryCode: Number(this.form.value.phoneStep.country.phoneCode).toString(),
                        floor: this.form.get('unitStep.floor').value,
                        apartment: this.form.get('unitStep.unit').value,
                        occupancyRole:  this.form.get('typeStep.consorcistaType').value
                    };

                    if(this.consorcio.hasTowers){
                        newconsorcista['tower'] =  this.form.get('unitStep.tower').value;
                    }

                    if(this.form.get('addConsorcistaStep.newConsorcistahasGarage')?.value){
                         newconsorcista['cocheras']=[withCocheraLabel({
                            consorcioId:this.consorcistaToken.consorcioId,
                            createdAt: new Date(),
                            nivel:this.form.get('addConsorcistaStep.newConsorcistaCochera.nivel').value,
                            numero:this.form.get('addConsorcistaStep.newConsorcistaCochera.numero').value,
                            occupancyRole:this.form.get('addConsorcistaStep.newConsorcistaCochera.occupancyRole').value,
                            firstName:this.form.get('addConsorcistaStep.newConsorcistaFirstName').value,
                            lastName:this.form.get('addConsorcistaStep.newConsorcistaLastName').value,
                            phoneArea:this.form.get('addConsorcistaStep.newConsorcistaPhoneArea').value,
                            phoneNumber:this.form.get('addConsorcistaStep.newConsorcistaPhoneNumber').value, 
                            countryCode:Number(this.form.value.phoneStep.country.phoneCode).toString(),
                        }, this.consorcio?.cocheras)]
                    }
                    this.consorcistasToTransfer.push(newconsorcista);  
                    //resetear formulario de agregar consorcista
                    this.form.get('addConsorcistaStep')?.reset();
                    this.nextStep('listConsorcistaStep');
                }
                break;
            case 'listConsorcistaStep':
                this.nextStep('queryStep');
                break;
            case 'last':
                this.confirmationService.confirm({
                    message: '¿Está seguro de que desea enviar el formulario?',
                    accept: () => {
                        this.submitForm();
                    }
                });
                break;
        }
    }

    async shouldAutoClose(): Promise<boolean> {
        try {
            const account = await firstValueFrom(this.userService.getAccount(this.consorcistaToken.administrationId, 'administration'));
            
            return account?.autoAceptRegistration === true;
        } catch (error) {
            return false;
        }
    }

    async submitForm() {
        if (!this.consorcistaToken) {
            this.errorMessage = {
                severity: 'error',
                detail: 'Código de registro inválido. Por favor, verifique el enlace que recibió o comuníquese con la administración del consorcio para obtener asistencia.',
            };
            return;
        }

        try {
            this.loaderStore.set('loading', true);
            //crear el consorcsita pendiente
            this.consorcistaPendiente.consorcioId = this.consorcistaToken.consorcioId;
            this.consorcistaPendiente.wspId = this.consorcistaToken.wspId;
            this.consorcistaPendiente.status = 'pending';
            this.consorcistaPendiente.createdAt = new Date();
            this.consorcistaPendiente.onlyGarage = this.form.get('unitStep.onlyGarage').value;
            this.consorcistaPendiente.role = ConsorcistaRole.Consorcista;
            if (await this.shouldAutoClose()) {
                this.consorcistaPendiente.autoClose = true;
            }
            await this.consorcistasService.createPending(this.consorcistaToken.consorcioId, this.consorcistaPendiente);
            //borrar el codigo utilizado
            await this.signupTokenService.delete(this.consorcistaToken.uid);
            //crear notificacion para el administrador
            await this.sendNotificationConsorcista(this.consorcistaPendiente.firstName, this.consorcistaPendiente.lastName);
            //redirigir a la home tras tocar el boton de ok del mensaje de exito
            this.showFinishDialog = true;
        }catch (error) {
            this.errorMessage = {
                severity: 'error',
                detail: 'Error al enviar el formulario, revisá e intentá de nuevo. Si el problema continua consultá con soporte.',
            };
        } finally {
            this.loaderStore.set('loading', false);
        }
    }

    async submitTransfer() {
        if (!this.consorcistaToken) {
            this.errorMessage = {
                severity: 'error',
                detail: 'Código de registro inválido. Por favor, verifique el enlace que recibió o comuníquese con la administración del consorcio para obtener asistencia.',
            };
            return;
        }

        try {
            
            this.loaderStore.set('loading', true);
            this.consorcistaPendiente.consorcioId = this.consorcistaToken.consorcioId;
            this.consorcistaPendiente.wspId = this.consorcistaToken.wspId;
            this.consorcistaPendiente.status = 'pending';
            this.consorcistaPendiente.createdAt = new Date();
            this.consorcistaPendiente.onlyGarage = false;
            this.consorcistaPendiente.role = ConsorcistaRole.Consorcista;

            this.consorcistasToTransfer.map(c=>{
                c.role = ConsorcistaRole.Consorcista;
                c.onlyGarage = false;
                c.createdAt = new Date();
                return c;
            });

            let newTransfer:newTransfer ={
                canal:'Whatsapp',
                administrationUserId: this.consorcistaToken.administrationId,
                apartment: this.form.get('unitStep.unit').value,
                floor: this.form.get('unitStep.floor').value,
                consorcistasNew: this.consorcistasToTransfer,
                maintainOwnership: false
            }

            if(this.consorcio.hasTowers){
                newTransfer.tower = this.form.get('unitStep.tower').value;
            }

            if (await this.shouldAutoClose()) {
                newTransfer.autoClose = true;
            }

            if(this.consorcistaPendiente.pendingInteraction){
                //queda guardado en la mudanza; se convierte en Complain/Question recién al aprobarla (ver resolveTransfer)
                newTransfer.pendingInteraction = this.consorcistaPendiente.pendingInteraction;
            }

            const transferCreated = await this.consorcioManager.createTransfer(newTransfer, {
                consorcio: this.consorcio,
                consorcista: this.consorcistaPendiente as any,
                forceStatus: 'actionRequired',
            });

            //borrar el codigo utilizado
            await this.signupTokenService.delete(this.consorcistaToken.uid);
            //crear notificacion para el administrador
            await this.sendNotificationTransfer(this.consorcistaPendiente.firstName, this.consorcistaPendiente.lastName);
            //redirigir a la home tras tocar el boton de ok del mensaje de exito
            this.showFinishDialog = true;
        }catch (error) {
            this.errorMessage = {
                severity: 'error',
                detail: 'Error al enviar el formulario, revisá e intentá de nuevo. Si el problema continua consultá con soporte.',
            };
        } finally {
            this.loaderStore.set('loading', false);
        }
    }

    pickedCochera(event:{nivel:number, numero:number}) {
        this.showAddCocheraDialog=false;
        const {nivel, numero} = event;

        //asignar valores al formulario
        this.form.get('addConsorcistaStep.newConsorcistaCochera.nivel').setValue(nivel.toString() || '0');
        this.form.get('addConsorcistaStep.newConsorcistaCochera.numero').setValue(numero);
    }

    async sendNotificationConsorcista(name:string, lastName:string) {
        await this.notificationService.createPush(
            {
                content: `Hizo click en el enlace de registro y completó el formulario.Te está esperando para ser aceptado.`,
                createdAt: new Date(),
                title: `Nuevo consorcista pendiente: ${name} ${lastName}. `,
                read: false,
                data: {
                    url: '/consorcios/owners/pending',
                },
            },
            this.consorcistaToken.administrationId,
            'administration'
        );
    }
    async sendNotificationTransfer(name:string, lastName:string) {
        await this.notificationService.createPush(
            {
                content: `Hizo click en el enlace de registro y completó el formulario.Te está esperando para ser aceptado.`,
                createdAt: new Date(),
                title: `Nueva mudanza pendiente: ${name} ${lastName} en el consorcio ${this.consorcio.name}. `,
                read: false,
                data: {
                    url: '/reclamos?type=transfer&consorcioName='+encodeURIComponent(this.consorcio.name),
                },
            },
            this.consorcistaToken.administrationId,
            'administration'
        );
    }
}
